/**
 * Task Types (Frontend)
 * Type definitions for task management
 */

export type TaskOriginType =
  | 'manual'
  | 'from_interaction'
  | 'from_case'
  | 'from_publication'
  | 'from_document';

export type TaskCategory =
  | 'PRAZO_PROCESSUAL'
  | 'ACAO_INTERNA'
  | 'FOLLOW_UP'
  | 'DOCUMENTO'
  | 'OUTRO';

export type TaskPriority =
  | 'LOW'
  | 'MEDIUM'
  | 'HIGH'
  | 'URGENT';

export type TaskStatus =
  | 'PENDING'
  | 'IN_PROGRESS'
  | 'WAITING'
  | 'FOR_REVIEW'
  | 'COMPLETED'
  | 'CANCELLED';

/**
 * Task entity
 */
export interface Task {
  id: number;
  title: string;
  description?: string;
  category: TaskCategory;
  priority: TaskPriority;
  status: TaskStatus;

  // Assignment
  assigned_user_id: string;
  assigned_user_name: string;

  // Review workflow
  reviewer_id?: string;
  reviewer_name?: string;

  // Relationships
  client_id?: string;
  client_name?: string;
  case_id?: string;
  case_title?: string;
  case_number?: string;
  interaction_id?: string;

  // Origin tracking
  origin_type?: TaskOriginType;
  origin_id?: string;
  origin_metadata?: Record<string, unknown>;

  // Dates
  due_date: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  reminded_at?: string;

  // Notifications
  notification_sent: boolean;
  alert_days_before: number;

  // Archive settings
  manual_archive: boolean;
  archived_at?: string; // Manual archiving timestamp

  // Audit
  created_by: string;
  created_by_name?: string;
  updated_by?: string;
  completed_by?: string;

  // Time tracking
  estimated_hours?: number;
  actual_hours?: number;

  // Legacy
  publication_id?: number;
  publication_title?: string;
  publication_process?: string;
  communication_id?: number;
  tags?: string[];
}

/**
 * Create Task DTO
 */
/** Campos v2 (modelo Notion) aceitos na criação e na edição. */
export interface TaskV2Fields {
  /** P0/P1/P2 — alternativa a `priority`. */
  prioridade?: 'P0' | 'P1' | 'P2';
  prazo_fatal?: string | null;
  prazo_interno?: string | null;
  attachment_url?: string | null;
  carteira_id?: string | null;
}

/** Compromisso de agenda criado na mesma ação (gaveta). */
export interface AgendaRequest {
  tipo: 'AUDIENCIA' | 'REUNIAO' | 'COMPROMISSO';
  data: string;
  inicio?: string | null;
  fim?: string | null;
  local?: string | null;
  titulo?: string | null;
}

export interface CreateTaskDto extends TaskV2Fields {
  title: string;
  description?: string;
  priority?: TaskPriority;
  agenda?: AgendaRequest;
  status?: TaskStatus;
  category?: TaskCategory;
  assigned_user_id?: string;
  reviewer_id?: string;

  // Relationships
  client_id?: string;
  client_name?: string;
  case_id?: string;
  case_title?: string;
  case_number?: string;
  interaction_id?: string;

  // Origin
  origin_type?: TaskOriginType;
  origin_id?: string;
  origin_metadata?: Record<string, unknown>;

  // Dates (pode vir do prazo interno/fatal)
  due_date?: string;

  // Time tracking
  estimated_hours?: number;

  // Notifications
  alert_days_before?: number;

  // Legacy
  publication_id?: number;
  tags?: string[];
}

/**
 * Update Task DTO
 */
export interface UpdateTaskDto extends TaskV2Fields {
  title?: string;
  description?: string;
  priority?: TaskPriority;
  status?: TaskStatus;
  category?: TaskCategory;
  assigned_user_id?: string;
  reviewer_id?: string;
  due_date?: string;
  estimated_hours?: number;
  actual_hours?: number;
  alert_days_before?: number;
  manual_archive?: boolean;
  tags?: string[];
}

/**
 * Complete Task DTO
 */
export interface CompleteTaskDto {
  actual_hours?: number;
  notes?: string;
}

/**
 * Cancel Task DTO
 */
export interface CancelTaskDto {
  reason: string;
}

/**
 * Reject Task DTO
 */
export interface RejectTaskDto {
  comment: string;
}

/**
 * Task filters for queries
 */
export interface TaskFilters {
  search?: string;
  status?: TaskStatus | TaskStatus[];
  priority?: TaskPriority | TaskPriority[];
  category?: TaskCategory | TaskCategory[];
  assigned_user_id?: string;
  reviewer_id?: string;
  client_id?: string;
  case_id?: string;
  interaction_id?: string;
  origin_type?: TaskOriginType;
  due_date_from?: string;
  due_date_to?: string;
  is_overdue?: boolean;
  notification_sent?: boolean;
}

/**
 * Pagination options
 */
export interface PaginationOptions {
  page?: number;
  limit?: number;
  sort?: string;
}

/**
 * Paginated response
 */
export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

/**
 * Task statistics
 */
export interface TaskStats {
  total: number;
  by_status: {
    pending: number;
    in_progress: number;
    for_review: number;
    completed: number;
    cancelled: number;
  };
  by_priority: {
    low: number;
    medium: number;
    high: number;
    urgent: number;
  };
  overdue: number;
  due_today: number;
  due_this_week: number;
}

/**
 * Task Metrics (used by TaskDashboard)
 */
export interface TaskMetrics {
  total: number;
  pending: number;
  inProgress: number;
  forReview: number;
  completed: number;
  cancelled: number;
  overdue: number;
  dueToday: number;
  completionRate: number;
  averageCompletionTime: number;
}

/**
 * Task Comment
 */
export interface TaskComment {
  id: number;
  task_id: number;
  author_id: string;
  author_name: string;
  /** AGENT = comentário do agente (Hermes). */
  author_kind?: 'HUMAN' | 'AGENT';
  text: string;
  created_at: string;
}

/**
 * API Response wrapper
 */
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  timestamp: string;
}

/**
 * Priority label mapping
 */
export const TASK_PRIORITY_LABELS: Record<TaskPriority, string> = {
  LOW: 'Baixa',
  MEDIUM: 'Média',
  HIGH: 'Alta',
  URGENT: 'Urgente'
};

/**
 * Priority colors for UI
 */
export const TASK_PRIORITY_COLORS: Record<TaskPriority, string> = {
  LOW: 'bg-muted text-muted-foreground',
  MEDIUM: 'bg-primary/10 text-primary',
  HIGH: 'bg-destructive/10 text-destructive',
  URGENT: 'bg-destructive/20 font-semibold text-destructive'
};

/**
 * Status label mapping
 */
export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  PENDING: 'Pendente',
  IN_PROGRESS: 'Em andamento',
  WAITING: 'Aguardando',
  FOR_REVIEW: 'Em Revisão',
  COMPLETED: 'Concluída',
  CANCELLED: 'Cancelada'
};

/**
 * Status colors for UI
 */
export const TASK_STATUS_COLORS: Record<TaskStatus, string> = {
  PENDING: 'bg-muted text-foreground',
  IN_PROGRESS: 'bg-primary/10 text-primary',
  WAITING: 'bg-secondary text-secondary-foreground',
  FOR_REVIEW: 'bg-primary/5 text-primary ring-1 ring-inset ring-primary/30',
  COMPLETED: 'bg-primary text-primary-foreground',
  CANCELLED: 'bg-muted text-muted-foreground'
};

/**
 * Category label mapping
 */
export const TASK_CATEGORY_LABELS: Record<TaskCategory, string> = {
  PRAZO_PROCESSUAL: 'Prazo Processual',
  ACAO_INTERNA: 'Ação Interna',
  FOLLOW_UP: 'Acompanhamento',
  DOCUMENTO: 'Documento',
  OUTRO: 'Outro'
};

/**
 * Category icons for UI
 */
export const TASK_CATEGORY_ICONS: Record<TaskCategory, string> = {
  PRAZO_PROCESSUAL: '⚖️',
  ACAO_INTERNA: '📝',
  FOLLOW_UP: '📞',
  DOCUMENTO: '📄',
  OUTRO: '📋'
};
